import { CheckCircle2, TrendingUp, Trophy, Phone, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "@/config/brand";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";
const green = "#8DC63F"; // Miss Rodriguez brand green

// Real live-client case study: Miss Rodriguez Pest Control (Clarissa R.)
// Built for < $2,000, went from ~3 leads/day to ~16 leads/day, ~50% close rate.
// Currently in consideration as PlugitAI's official spokesperson.

const METRICS = [
  { label: "Build cost", value: "< $2,000", note: "One-time, everything included" },
  { label: "First 10 days", value: "~3 / day", note: "Leads inbound while indexing" },
  { label: "Now (steady state)", value: "~16 / day", note: "After visibility grew" },
  { label: "Close rate", value: "~50%", note: "Before referrals compound" },
];

const INCLUDED = [
  "250–400 professionally built pages",
  "Mobile-first responsive design",
  "AI chatbot that engages visitors 24/7",
  "Instant quote request system",
  "Lead capture throughout the site",
  "Review management",
  "Fast-loading, Google-friendly pages",
  "Professional branding + custom graphics",
  "Service page for every service offered",
  "Individual pest pages for every major pest",
  "Local SEO page for every city served",
  "Educational pest library",
  "Special offers and promotions",
  "Contact forms, click-to-call, appointment requests",
];

const TECH_SEO = [
  "Schema markup",
  "XML sitemap",
  "Optimized metadata",
  "Image optimization",
  "Internal linking",
  "Core Web Vitals",
  "Google indexing setup",
  "Local Business optimization",
];

const AI_FEATURES = [
  "24/7 AI receptionist",
  "Lead qualification",
  "Instant customer responses",
  "Appointment scheduling",
  "Automated follow-up",
  "CRM-ready integrations",
];

const PAGE_BREAKDOWN = [
  { label: "Home",                                             pages: "1" },
  { label: "About",                                            pages: "1" },
  { label: "Contact",                                          pages: "1" },
  { label: "Financing / Specials / Reviews / FAQ",             pages: "8–12" },
  { label: "Residential + commercial services",                pages: "15–20" },
  { label: "Individual pest pages",                            pages: "25–35" },
  { label: "City SEO pages",                                   pages: "75–150" },
  { label: "Service × City combos (e.g. Termite Control Plano)", pages: "75–150" },
  { label: "Blog / pest library",                              pages: "50–100" },
];

const SEO_EXAMPLES = [
  "Dallas Pest Control",
  "Plano Termite Control",
  "Bed Bug Treatment Frisco",
  "Rodent Removal McKinney",
  "Flea Control Arlington",
  "Ant Exterminator Grand Prairie",
];

export default function MissRodriguezCaseStudy() {
  return (
    <section
      className="relative z-10 py-20 md:py-28"
      style={{ background: "transparent" }}
      data-testid="miss-rodriguez-case-study"
    >
      {/* Ambient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(900px 500px at 20% 20%, ${green}18, transparent 60%),
                       radial-gradient(700px 400px at 80% 80%, ${teal}14, transparent 70%)`,
        }}
      />

      <div className="mx-auto max-w-[1200px] px-6 md:px-10 relative">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.24em]"
            style={{ background: `${green}18`, color: green, border: `1px solid ${green}66` }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: green, boxShadow: `0 0 8px ${green}` }} />
            Real client · real numbers
          </div>
          <h2
            className="mt-4 font-black tracking-[-0.02em] leading-[1.02]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.6rem)", color: "white" }}
          >
            Miss Rodriguez Pest Control —{" "}
            <span
              style={{
                backgroundImage: `linear-gradient(135deg, ${green} 0%, ${gold} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              built for under $2,000
            </span>
            .
          </h2>
          <p className="mt-3 max-w-[720px] mx-auto text-sm md:text-base" style={{ color: C.textDim }}>
            This is a live, currently-running site we built for <strong className="text-white">Clarissa R.</strong> in the Dallas–Fort Worth pest control market. The numbers below are hers.
          </p>
        </div>

        {/* Two screenshots side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <FigureCard
            src="/showcase/miss-rodriguez-home.png"
            alt="Miss Rodriguez Pest Control homepage — DFW pest control site built by PlugitAI"
            kicker="Homepage"
            title="Same-day service · honest pricing · satisfaction guaranteed"
            note="Full hero with 12 pest-category service tiles, embedded quote form, and a 24/7 AI receptionist. Mobile-first responsive."
            accent={green}
            testid="mr-home-figure"
          />
          <FigureCard
            src="/showcase/miss-rodriguez-bugs.png"
            alt="Miss Rodriguez Pest Control — pest library grid page"
            kicker="Pest Library"
            title="27+ individual pest pages · every major pest, its own SEO page"
            note="Each card links to a dedicated pest page — separate URL, separate metadata, its own local variants (e.g., Bed Bug Treatment Frisco)."
            accent={green}
            testid="mr-bugs-figure"
          />
        </div>

        {/* Metrics strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-10 md:mt-14">
          {METRICS.map((m) => (
            <div
              key={m.label}
              className="rounded-2xl p-4 md:p-5 border"
              style={{
                borderColor: `${green}55`,
                background: "rgba(11,21,36,0.72)",
                backdropFilter: "blur(10px)",
                boxShadow: `0 30px 90px -50px ${green}66`,
              }}
              data-testid={`mr-metric-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color: green }}>
                {m.label}
              </div>
              <div className="mt-1 text-3xl md:text-4xl font-black text-white leading-none">{m.value}</div>
              <div className="mt-2 text-[11px] leading-snug" style={{ color: C.textFade }}>{m.note}</div>
            </div>
          ))}
        </div>

        {/* Clarissa callout */}
        <div
          className="mt-8 md:mt-10 rounded-3xl p-6 md:p-8 border flex items-start gap-4"
          style={{
            borderColor: `${gold}66`,
            background: `linear-gradient(135deg, ${green}0e 0%, ${gold}10 100%), rgba(11,21,36,0.72)`,
            boxShadow: `0 60px 160px -60px ${gold}44`,
          }}
        >
          <div
            className="shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center"
            style={{ background: `${gold}22`, border: `1.5px solid ${gold}`, color: gold }}
          >
            <Trophy size={20} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color: gold }}>
              Face of PlugitAI · spokesperson finalist
            </div>
            <p className="mt-2 text-base md:text-lg leading-relaxed text-white/90">
              <strong className="text-white">Not bad, Clarissa R.</strong> You just might become the face of PlugitAI. We&apos;re currently interviewing several clients to be our official spokesperson — and you&apos;ve definitely earned a spot in that conversation.
            </p>
          </div>
        </div>

        {/* What you get with Kit & the PlugitAI team */}
        <div className="mt-16 md:mt-24">
          <div className="text-center mb-8">
            <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color: teal }}>
              What you get with Kit &amp; the PlugitAI team
            </div>
            <h3 className="mt-3 font-black tracking-[-0.02em] leading-[1.02]" style={{ fontSize: "clamp(1.6rem, 3.4vw, 2.6rem)", color: "white" }}>
              We don&apos;t build websites.{" "}
              <span style={{
                backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>
                We build businesses that get found, trusted, and chosen.
              </span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            <FeatureCard title="Your website includes"     accent={green} items={INCLUDED}     testid="mr-included" />
            <FeatureCard title="Technical SEO"             accent={teal}  items={TECH_SEO}    testid="mr-techseo" />
            <FeatureCard title="AI-powered features"       accent={gold}  items={AI_FEATURES} testid="mr-ai" />
          </div>
        </div>

        {/* SEO strategy — hundreds of pages */}
        <div
          className="mt-14 md:mt-20 rounded-3xl p-6 md:p-10 border overflow-hidden relative"
          style={{
            borderColor: `${teal}44`,
            background: "rgba(11,21,36,0.72)",
            backdropFilter: "blur(10px)",
            boxShadow: `0 60px 160px -60px ${teal}55`,
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color: teal }}>
            SEO that goes beyond rankings
          </div>
          <h3 className="mt-2 font-black tracking-[-0.02em] leading-[1.02]" style={{ fontSize: "clamp(1.4rem, 3vw, 2.2rem)", color: "white" }}>
            Instead of hoping one page ranks, we build hundreds targeting exactly what customers are searching for.
          </h3>

          <div className="mt-6 flex flex-wrap gap-2">
            {SEO_EXAMPLES.map((s) => (
              <span
                key={s}
                className="text-[12px] font-semibold px-3 py-1.5 rounded-full"
                style={{ background: `${teal}18`, border: `1px solid ${teal}55`, color: "white" }}
              >
                {s}
              </span>
            ))}
            <span
              className="text-[12px] font-semibold px-3 py-1.5 rounded-full"
              style={{ background: `${gold}18`, border: `1px solid ${gold}55`, color: gold }}
            >
              … every city, every service, every opportunity
            </span>
          </div>

          {/* Page breakdown table */}
          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.22em] font-black mb-3" style={{ color: C.textDim }}>
              Authority-site page breakdown · 250–400 pages
            </div>
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: C.line, background: "rgba(0,0,0,0.25)" }}>
              {PAGE_BREAKDOWN.map((row, i) => (
                <div key={row.label}
                     className="grid grid-cols-[1fr,110px] md:grid-cols-[1fr,140px] items-center px-4 md:px-5 py-3"
                     style={{ borderTop: i === 0 ? "none" : `1px solid ${C.line}` }}>
                  <div className="text-sm md:text-[15px] text-white/90">{row.label}</div>
                  <div className="text-right font-mono font-bold" style={{ color: teal }}>{row.pages}</div>
                </div>
              ))}
              <div
                className="grid grid-cols-[1fr,110px] md:grid-cols-[1fr,140px] items-center px-4 md:px-5 py-3 border-t"
                style={{ borderColor: C.line, background: `${teal}14` }}
              >
                <div className="text-sm md:text-[15px] font-black text-white">Estimated total</div>
                <div className="text-right font-mono font-black" style={{ color: gold }}>250–400+</div>
              </div>
            </div>
            <p className="mt-3 text-[12px] leading-relaxed" style={{ color: C.textFade }}>
              Same authority approach we&apos;ve used on other PlugitAI builds. Every page becomes another opportunity to rank in Google and bring in leads — especially in a hyper-local market like DFW.
            </p>
          </div>
        </div>

        {/* The Kit difference */}
        <div className="mt-14 md:mt-20 rounded-3xl p-6 md:p-10 border relative overflow-hidden"
             style={{
               borderColor: `${green}55`,
               background: `linear-gradient(135deg, ${green}0e 0%, ${teal}0e 100%), rgba(11,21,36,0.72)`,
               boxShadow: `0 60px 160px -60px ${green}55`,
             }}
             data-testid="mr-kit-difference">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} style={{ color: green }} />
            <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color: green }}>
              The Kit difference
            </div>
          </div>
          <h3 className="font-black tracking-[-0.02em] leading-[1.05]" style={{ fontSize: "clamp(1.4rem, 3vw, 2.2rem)", color: "white" }}>
            Most agencies build 10–20 pages and call it a website. We build complete digital ecosystems designed to generate leads around the clock.
          </h3>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              "Every page has a purpose.",
              "Every click is guided toward becoming a customer.",
              "Every feature is built to help your business grow — not just look good.",
            ].map((line) => (
              <div key={line}
                   className="rounded-2xl p-4 border text-sm text-white/90 leading-snug"
                   style={{ borderColor: `${green}33`, background: `${green}0e` }}>
                {line}
              </div>
            ))}
          </div>
          <p className="mt-6 text-base md:text-lg font-semibold" style={{ color: green }}>
            This isn&apos;t a website. It&apos;s a sales machine built to work for you 24/7.
          </p>
        </div>

        {/* Final CTA */}
        <div className="mt-10 md:mt-14 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/case-studies/miss-rodriguez"
            className="inline-flex items-center gap-2 px-5 h-12 rounded-full font-black text-sm"
            style={{
              background: `${green}18`,
              border: `1px solid ${green}77`,
              color: "white",
            }}
            data-testid="mr-full-case-study-btn"
          >
            Read the full case study <ExternalLink size={14} />
          </Link>
          <a
            href="tel:+12148155966"
            className="inline-flex items-center gap-2 px-6 h-12 rounded-full font-black text-sm"
            style={{
              background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
              color: "white",
              boxShadow: `0 20px 50px -20px ${teal}aa`,
            }}
            data-testid="mr-quote-btn"
          >
            <Phone size={14} /> Get a real quote — (214) 815-5966
          </a>
        </div>
      </div>
    </section>
  );
}

function FigureCard({ src, alt, kicker, title, note, accent, testid }) {
  return (
    <figure
      className="relative rounded-3xl overflow-hidden border group"
      style={{
        borderColor: `${accent}55`,
        background: "rgba(11,21,36,0.72)",
        backdropFilter: "blur(10px)",
        boxShadow: `0 40px 120px -40px ${accent}66`,
      }}
      data-testid={testid}
    >
      <div className="relative overflow-hidden" style={{ aspectRatio: "3 / 4" }}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
        />
        {/* Live chip */}
        <div
          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[9px] font-black uppercase tracking-[0.22em]"
          style={{ background: accent, color: "#0B1524", boxShadow: `0 6px 18px -6px ${accent}aa` }}
        >
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#0B1524" }} />
          Live site
        </div>
        {/* Bottom scrim + kicker */}
        <div
          className="absolute left-0 right-0 bottom-0 px-3 md:px-4 py-2 md:py-3 flex items-center justify-between"
          style={{ background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.9) 100%)" }}
        >
          <span
            className="text-[10px] font-black uppercase tracking-[0.24em]"
            style={{ color: accent }}
          >
            {kicker}
          </span>
          <span className="text-[10px] font-mono text-white/80 truncate ml-2">Live site · DFW</span>
        </div>
      </div>
      <figcaption className="p-4 md:p-5">
        <div className="font-black text-white text-[15px] md:text-base leading-tight">{title}</div>
        <div className="mt-1.5 text-[12px] md:text-[13px] leading-relaxed" style={{ color: "rgba(255,255,255,0.6)" }}>
          {note}
        </div>
      </figcaption>
    </figure>
  );
}

function FeatureCard({ title, items, accent, testid }) {
  return (
    <div
      className="rounded-2xl p-5 border"
      style={{
        borderColor: `${accent}55`,
        background: "rgba(11,21,36,0.72)",
        backdropFilter: "blur(10px)",
      }}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color: accent }}>
        {title}
      </div>
      <ul className="mt-3 space-y-1.5">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-[13px] leading-snug text-white/90">
            <CheckCircle2 size={13} className="shrink-0 mt-0.5" style={{ color: accent }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
